import { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import i18n from '../../i18n';

interface Props {
  children: ReactNode;
  moduleName?: string;
  /** When true the fallback fits inline rather than filling the viewport */
  inline?: boolean;
  /** Custom fallback to render instead of the default card */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  showDetail: boolean;
}

const IS_DEV = import.meta.env.DEV;

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, showDetail: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    const module = this.props.moduleName ?? 'unknown';

    // Always log to console — visible in browser DevTools and Netlify log drain
    console.error(
      `[ErrorBoundary] Crash in module "${module}" at ${window.location.pathname}\n`,
      error,
      '\nComponent stack:',
      info.componentStack,
    );

    // Non-blocking session storage ring-buffer (50 entries)
    try {
      const stored = sessionStorage.getItem('error_log');
      const logs: unknown[] = stored ? JSON.parse(stored) : [];
      logs.push({
        message:        error.message,
        stack:          error.stack?.slice(0, 2000),
        componentStack: info.componentStack?.slice(0, 2000),
        module,
        url:            window.location.href,
        timestamp:      new Date().toISOString(),
        // Pull user context from a known global if available
        userRole:       (window as Record<string, unknown>).__nizam_role as string | undefined,
        companyId:      (window as Record<string, unknown>).__nizam_company as string | undefined,
      });
      if (logs.length > 50) logs.shift();
      sessionStorage.setItem('error_log', JSON.stringify(logs));
    } catch {
      // Storage full or unavailable — do not cascade
    }

    // Non-blocking DB telemetry via dynamically-imported monitor
    import('../../components/ui/ErrorMonitor').then(({ logSystemError, incrementCrashCount }) => {
      incrementCrashCount(module);
      logSystemError({
        module,
        page:          window.location.pathname,
        error_type:    'render_crash',
        error_message: `${error.message} | component: ${info.componentStack?.split('\n')[1]?.trim() ?? ''}`.slice(0, 2000),
        user_role:     (window as Record<string, unknown>).__nizam_role as string | undefined,
        company_id:    (window as Record<string, unknown>).__nizam_company as string | undefined,
      });
    }).catch(() => { /* non-blocking */ });
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null, showDetail: false });
    window.location.reload();
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null, showDetail: false });
  };

  toggleDetail = () => {
    this.setState(s => ({ showDetail: !s.showDetail }));
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback)  return this.props.fallback;

    const { moduleName, inline } = this.props;
    const { error, showDetail } = this.state;
    const t = i18n.t.bind(i18n);

    // ── Inline variant: used by SafeWidget for sub-section failures ──────────
    if (inline) {
      return (
        <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-red-400 font-medium">
              {moduleName ? `${moduleName} failed to load` : 'Section error'}
            </p>
            {IS_DEV && error && (
              <p className="text-xs text-red-400/70 mt-1 font-mono truncate" title={error.message}>
                {error.message}
              </p>
            )}
          </div>
          <button
            onClick={this.handleRetry}
            className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white flex-shrink-0 underline underline-offset-2"
          >
            Retry
          </button>
        </div>
      );
    }

    // ── Full-page variant: used by LazyRoute at module level ─────────────────
    return (
      <div className="min-h-[400px] flex items-center justify-center p-8">
        <div className="max-w-md w-full bg-slate-100/60 dark:bg-slate-800/60 border border-slate-300 dark:border-slate-700 rounded-xl p-8 text-center">
          <div className="w-14 h-14 bg-red-500/10 border border-red-500/20 rounded-full flex items-center justify-center mx-auto mb-5">
            <AlertTriangle className="w-7 h-7 text-red-400" />
          </div>

          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">
            {t('common.something_went_wrong')}
          </h2>

          {moduleName && (
            <p className="text-xs text-slate-600 dark:text-slate-500 mb-1">
              {t('common.module')}: {moduleName}
            </p>
          )}

          {/* Show real error message in dev; show generic message in prod */}
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            {IS_DEV && error
              ? error.message
              : t('common.unexpected_error_message')
            }
          </p>

          {/* Dev-only collapsible stack trace */}
          {IS_DEV && error?.stack && (
            <div className="mb-4 text-start">
              <button
                onClick={this.toggleDetail}
                className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors mb-1"
              >
                {showDetail ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {showDetail ? 'Hide' : 'Show'} stack trace
              </button>
              {showDetail && (
                <pre className="text-[10px] text-slate-600 dark:text-slate-500 bg-white dark:bg-slate-900 rounded p-3 overflow-auto max-h-40 text-start whitespace-pre-wrap break-all">
                  {error.stack}
                </pre>
              )}
            </div>
          )}

          {/* Route + timestamp for prod bug reports */}
          <p className="text-[10px] text-slate-700 dark:text-slate-600 mb-5 font-mono">
            {window.location.pathname} · {new Date().toISOString().slice(0, 19)}
          </p>

          <div className="flex gap-3 justify-center">
            <button
              onClick={this.handleRetry}
              className="px-4 py-2 text-sm font-medium bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-lg transition-colors"
            >
              {t('common.try_again')}
            </button>
            <button
              onClick={this.handleReload}
              className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              {t('common.reload_page')}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
